import { useCallback, useSyncExternalStore } from 'react';

/**
 * Menú lateral contraído (solo íconos) o extendido. Sin preferencia guardada decide el equipo: en televisores
 * (Silk de Fire TV, Tizen, webOS, Android TV…) y pantallas angostas empieza contraído para dejar espacio a los
 * diagramas; en escritorio, extendido. Solo se guarda la elección que se aparta de esa regla, así que volver a la
 * posición automática vuelve a seguir al equipo.
 */
const STORAGE_KEY = 'nav';
const NARROW = '(max-width: 1023px)';

/** Navegadores de televisor. "AFT…" son los modelos Fire TV; las tabletas Fire (también con Silk) usan "KF…". */
const TV_UA = /\bAFT[A-Z0-9]+\b|SmartTV|SMART-TV|Tizen|Web0S|webOS\.TV|NetCast|HbbTV|BRAVIA|Android ?TV|GoogleTV|CrKey|AppleTV|Roku|VIDAA|Viera|Opera TV/i;

export const isTvBrowser = (ua: string) => TV_UA.test(ua);

export function autoCollapsed(ua: string, narrow: boolean): boolean {
  return narrow || isTvBrowser(ua);
}

type Saved = 'collapsed' | 'expanded' | null;

function readSaved(): Saved {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'collapsed' || v === 'expanded' ? v : null;
  } catch {
    return null;
  }
}

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

function subscribe(fn: () => void) {
  listeners.add(fn);
  const mq = window.matchMedia?.(NARROW);
  mq?.addEventListener('change', fn);
  return () => {
    listeners.delete(fn);
    mq?.removeEventListener('change', fn);
  };
}

/** Respaldo de la elección en esta visita si el navegador no deja guardar. */
let memory: boolean | null = null;

function snapshot(): boolean {
  if (memory !== null) return memory;
  const saved = readSaved();
  if (saved) return saved === 'collapsed';
  return autoCollapsed(navigator.userAgent, window.matchMedia?.(NARROW).matches ?? false);
}

/** `[contraído, alternar]`; alternar recuerda la elección en este navegador. */
export function useNavCollapsed(): [boolean, () => void] {
  const collapsed = useSyncExternalStore(subscribe, snapshot);
  const toggle = useCallback(() => {
    const next = !snapshot();
    try {
      localStorage.removeItem(STORAGE_KEY);
      if (next !== snapshot()) localStorage.setItem(STORAGE_KEY, next ? 'collapsed' : 'expanded');
    } catch {
      /* almacenamiento no disponible: la elección no se recuerda */
      memory = next;
    }
    notify();
  }, []);
  return [collapsed, toggle];
}
